// BotActionSheet — lifted out of the LiveTrading component body (God 2026-08-26,
// phase 4). Mobile bottom-sheet: webhook / pricer / test for one bot.
//
// Phases 1-3 moved top-level declarations, which close over nothing. This block
// lived INSIDE the component and closed over its state, so it could not be moved
// verbatim — it needed a prop for every name it borrowed. That prop list was
// computed by intersecting the block's identifiers with the component's scope
// (tools/refactor_livetrading_phase4.py), not written by hand: a hand-listed
// prop set is exactly where a missed dependency becomes a runtime undefined that
// the compiler never sees.
//
// Every prop keeps its ORIGINAL LOCAL NAME, so the JSX below is the original
// text. Behaviour is unchanged by construction.

import React from 'react';
import { BarChart2, Crosshair, Eye } from 'lucide-react';
import { toPanelSym } from './foundation';
import { ExecControl } from '../components/ui/ExecControl';

export interface BotActionSheetProps {
  botMenuId: any;
  deployments: any;
  fireDirectOrder: any;
  getLotSize: any;
  openBotDepth: any;
  setBotMenuId: any;
  setPricerSymbol: any;
  setSelectedBotForWebhook: any;
  setShowOptionPricer: any;
  setShowWebhookModal: any;
  testWebhook: any;
  setConfirmDelete?: any;
}

export const BotActionSheet: React.FC<BotActionSheetProps> = ({ botMenuId, deployments, fireDirectOrder, getLotSize, openBotDepth, setBotMenuId, setPricerSymbol, setSelectedBotForWebhook, setShowOptionPricer, setShowWebhookModal, testWebhook, setConfirmDelete }) => (
  <>
        {/* 📱 ••• bottom-sheet — WEBHOOK / PRICER / TEST for the selected bot */}
        {botMenuId && (() => {
          const d = deployments.find(x => x.id === botMenuId);
          if (!d) return null;
          const close = () => setBotMenuId(null);
          return (
            <div className="lt-sheet-overlay" onClick={close}>
              <div className="lt-sheet" onClick={e => e.stopPropagation()}>
                <div style={{fontSize:12,fontWeight:900,marginBottom:10,color:'var(--text,#e2e8f0)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  {d.name}
                  <button onClick={close} style={{background:'none',border:'none',color:'var(--text-muted,#64748b)',fontSize:18,cursor:'pointer',minWidth:44,minHeight:44}}>✕</button>
                </div>
                {d.webhookEnabled && (
                  <button onClick={()=>{close(); setSelectedBotForWebhook(d.id); setShowWebhookModal(true);}}
                    style={{background:'linear-gradient(135deg,#7c3aed,#a855f7)',color:'#fff'}}>
                    <Eye size={16}/> WEBHOOK — setup &amp; URL
                  </button>
                )}
                <button onClick={()=>{close(); setPricerSymbol(toPanelSym(d.assets[0]?.baseSymbol || d.assets[0]?.symbol || 'NIFTY')); setShowOptionPricer(true);}}
                  style={{background:'linear-gradient(135deg,#7c3aed,#5b21b6)',color:'#fff'}}>
                  <Crosshair size={16}/> PRICER — smart option pricer
                </button>
                {/* M-H2 (God D25): LIVE TEST = a REAL Dhan BUY — never on a tap. Hold 1.4 s. */}
                {d.tradingMode === 'LIVE' ? (
                  <ExecControl live tone="info" title="LIVE: real Dhan BUY — hold 1.4 s" onConfirm={()=>{
                    close();
                    const sym = d.assets[0]?.baseSymbol || 'NIFTY';
                    const optType = d.assets[0]?.optType || 'CE';
                    const qty = d.assets[0]?.qty || getLotSize(sym);
                    fireDirectOrder(sym, 'BUY', qty, 'LIVE', optType, 'ATM', d.product, d.id);
                  }}>
                    🧪 HOLD 1.4 s · LIVE TEST — real direct order
                  </ExecControl>
                ) : (
                  <button onClick={()=>{ close(); testWebhook(d.id); }} style={{background:'linear-gradient(135deg,#0891b2,#0e7490)',color:'#fff'}}>
                    🧪 TEST — webhook test
                  </button>
                )}
                <button onClick={()=>{ close(); openBotDepth(d); }}
                  style={{background:'linear-gradient(135deg,#0d9488,#0f766e)',color:'#fff'}}>
                  <BarChart2 size={16}/> DEPTH — 20-level order book
                </button>
                {/* DELETE moved here on phones (God 2026-09-24): on the card it sat
                    thumb-width from STOP. Kept last, apart, and still behind the
                    existing confirm dialog. */}
                {setConfirmDelete && (
                  <button onClick={()=>{ close(); setConfirmDelete(d.id); }}
                    style={{background:'transparent',color:'#f87171',border:'1px solid rgba(239,68,68,.45)',marginTop:10}}>
                    🗑 DELETE BOT — permanent (asks to confirm)
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        <style>{`
          .lt-scroll::-webkit-scrollbar{width:4px;height:4px}.lt-scroll::-webkit-scrollbar-thumb{background:#2d3748;border-radius:4px}.lt-scroll::-webkit-scrollbar-track{background:#0d1117}
          .lt-btn{border:none;border-radius:8px;cursor:pointer;font-weight:800;letter-spacing:1px;text-transform:uppercase;display:flex;align-items:center;justify-content:center;gap:5px;transition:all .2s}
          .lt-btn:hover{transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,0,0,.4)}
          .lt-inp{background:#060810;border:1px solid #1a1f3a;border-radius:8px;padding:8px 10px;color:#e2e8f0;font-size:11px;font-weight:600;outline:none;width:100%;transition:all .2s}
          .lt-inp:focus{border-color:#3b82f6;box-shadow:0 0 0 2px rgba(59,130,246,.2)}
          .stat-card{background:linear-gradient(135deg,#1e293b,#0f172a);padding:7px 12px;border-radius:8px;border:1px solid #1e293b;cursor:pointer;transition:all .2s;position:relative;overflow:hidden}
          .stat-card:hover{transform:translateY(-2px);border-color:#3b82f6;box-shadow:0 8px 24px rgba(59,130,246,.15)}
          .stat-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,currentColor,transparent);opacity:.5}
          .chip{display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:6px;font-size:9px;font-weight:700;margin-right:4px;margin-bottom:4px;cursor:pointer;transition:all .15s}
          .chip.active{background:linear-gradient(135deg,#4f46e5,#6366f1);color:#fff;box-shadow:0 2px 8px rgba(79,70,229,.4)}
          .chip.inactive{background:#0d1117;border:1px solid #1e293b;color:#64748b}
          .chip:hover{transform:translateY(-1px)}
          .tab-btn{padding:8px 16px;border:none;background:transparent;color:#64748b;font-weight:700;cursor:pointer;border-bottom:2px solid transparent;transition:.2s;font-size:11px}
          .tab-btn.active{color:#fff;border-bottom-color:#3b82f6}
          .export-btn{background:rgba(51,65,85,.5);padding:6px 12px;border-radius:6px;color:#fff;font-size:10px;border:1px solid #475569;display:flex;gap:6px;align-items:center;cursor:pointer;transition:.2s}
          .export-btn:hover{background:#3b82f6;border-color:#60a5fa}
          .lot-btn{background:#1e293b;border:1px solid #334155;color:#fbbf24;width:28px;height:28px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-weight:800;transition:.15s}
          .lot-btn:hover{background:#334155;border-color:#fbbf24}
          .strike-cell:hover{background:rgba(251,191,36,.15) !important;color:#fbbf24 !important}
          @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
          .pulse-dot{animation:pulse 2s infinite}
          @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
          .shimmer{background:linear-gradient(90deg,transparent 0%,rgba(168,85,247,.15) 50%,transparent 100%);background-size:200% 100%;animation:shimmer 3s infinite}
          .smart-btn{background:linear-gradient(135deg,#a855f7,#7c3aed);color:#fff;padding:7px 14px;border-radius:8px;font-size:10px;font-weight:800;letter-spacing:1px;border:none;cursor:pointer;display:flex;align-items:center;gap:6px;transition:all .2s;position:relative;overflow:hidden}
          .smart-btn:hover{transform:translateY(-1px);box-shadow:0 6px 20px rgba(168,85,247,.4)}
          .smart-btn::before{content:'';position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,.2),transparent);transform:translateX(-100%);transition:.4s}
          .smart-btn:hover::before{transform:translateX(100%)}
          /* ═══ THEME TOKENS (dark-native) + smart-invert LIGHT mode ═══
             Tokens stay DARK always (the app is dark-native — mixing light text
             tokens onto dark inline surfaces made names invisible). LIGHT mode
             instead smart-inverts the whole page (hue-rotate keeps green/red
             PNL semantics), so EVERY panel is readable — no half-light mix. */
          :root {
            --bg:#080b12; --surface:#0f172a; --surface-2:#1e293b; --border:#1e293b;
            --text:#e2e8f0; --text-muted:#64748b;
            --accent:#7c3aed; --success:#10b981; --danger:#ef4444; --warn:#f59e0b;
          }
          /* The invert lives on <html> (class jc-lt-on while this page is mounted),
             NOT on .lt-root (God 2026-09-24, iPhone audit). A filter on any other
             element becomes the containing block of every position:fixed child, so
             in LIGHT / AUTO-light mode every bottom sheet, modal and the KILL bar
             opened at the bottom of the 2,700-px page - off screen (measured: sheet
             top 2,304 px on a 664-px viewport). The root element is the one place
             the spec exempts, so fixed stays fixed. */
          html.jc-lt-on[data-jc-theme='light']{filter:invert(0.94) hue-rotate(180deg)}
          html.jc-lt-on[data-jc-theme='light'] img,
          html.jc-lt-on[data-jc-theme='light'] video{filter:invert(1) hue-rotate(180deg)}
          @media (prefers-color-scheme: light) {
            html.jc-lt-on[data-jc-theme='auto']{filter:invert(0.94) hue-rotate(180deg)}
            html.jc-lt-on[data-jc-theme='auto'] img,
            html.jc-lt-on[data-jc-theme='auto'] video{filter:invert(1) hue-rotate(180deg)}
          }
          .lt-root{background:var(--bg) !important;color:var(--text) !important}
          .bot-stats-row b{color:var(--text,#e2e8f0)}

          /* Bot-card stats — ALWAYS one horizontal 3-cell row (PNL/TRADES/W:L) */
          .bot-stats-row{display:flex;gap:12px;align-items:center}
          .bot-stats-row>div{display:flex;flex-direction:row;align-items:baseline;gap:5px;min-width:0}
          .bot-stats-row label{font-size:8px;font-weight:800;letter-spacing:1px;color:var(--text-muted,#64748b);text-transform:uppercase}
          .bot-stats-row b{font-size:11px;font-weight:800;font-family:ui-monospace,monospace;white-space:nowrap}

          /* Modals must cover the app chrome (top header + bottom bar are z 99999) —
             otherwise the modal's own "✕ close" header hides behind them on mobile */
          .lt-modal-overlay{z-index:100001 !important}

          /* Bottom sheet (mobile ••• menu) */
          .lt-sheet-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:100002;display:flex;align-items:flex-end}
          .lt-sheet{width:100%;background:var(--surface,#0f172a);border-top:1px solid var(--border,#1e293b);
            border-radius:16px 16px 0 0;padding:14px 16px calc(16px + env(safe-area-inset-bottom));}
          .lt-sheet button{width:100%;min-height:48px;display:flex;align-items:center;gap:10px;border:none;
            border-radius:10px;margin-bottom:8px;font-size:13px;font-weight:800;letter-spacing:.5px;cursor:pointer}

          /* 📱 Mobile: trim heavy modal side-padding so content fits phones */
          @media (max-width: 768px) {
            /* room for the KILL/DEPLOY bar that now rides on the app nav */
            .lt-root { padding-bottom: 72px !important; }
            .lt-scroll { padding-left: 10px !important; padding-right: 10px !important; }
            .lt-modal-box { width: 100% !important; max-width: 100% !important; max-height: 94vh !important; }
            .lt-modal-overlay { padding: 8px !important; }
          }
          /* 📱 ≤480px — touch-first: 44px targets, no horizontal overflow,
             full-screen modals, safe-area, single-column cards */
          @media (max-width: 480px) {
            .lt-root{overflow-x:hidden !important}
            .lt-root *{min-width:0}
            /* Header: title block on top, controls in a tidy 3-col grid below,
               meta chips become a horizontal scroll strip — no overlap */
            .lt-hdr{flex-direction:column !important;align-items:stretch !important;gap:10px;padding:10px 12px !important}
            .lt-hdr-controls{display:grid !important;grid-template-columns:repeat(3,1fr);gap:6px !important}
            .lt-hdr-controls>*{width:100%;min-height:44px;justify-content:center}
            .lt-hdr-meta{flex-wrap:nowrap !important;overflow-x:auto;-webkit-overflow-scrolling:touch;padding-bottom:2px}
            .lt-hdr-meta>span{white-space:nowrap;flex-shrink:0}
            .lt-btn{min-height:44px;padding:10px 12px !important;font-size:10px !important}
            .tab-btn{min-height:44px}
            .export-btn{min-height:40px}
            .stat-card{min-height:56px}
            .lt-modal-overlay{padding:0 !important;align-items:stretch !important}
            .lt-modal-box{width:100vw !important;max-width:100vw !important;height:100dvh !important;
              max-height:100dvh !important;border-radius:0 !important;
              padding-bottom:env(safe-area-inset-bottom) !important}
            .bot-actions{width:100% !important;justify-content:stretch !important;margin-top:8px}
            .bot-actions .lt-btn{flex:1}
            .bot-card-flex{flex-direction:column !important}
            /* 2026 phone pass (God 2026-09-24, iPhone audit) */
            .lt-hide-m{display:none !important}
            .lt-hdr-sec{display:none !important}
            .lt-show-m{display:flex !important}
            .lt-hdr-controls{grid-template-columns:repeat(2,1fr) !important}
            .lt-hdr-controls>*{display:flex;align-items:center;justify-content:center}
            .lt-hdr-meta{display:none !important}
            .bot-stats-row{display:grid !important;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px !important}
            .bot-stats-row>div{flex-direction:column !important;align-items:flex-start !important;gap:1px !important}
            .bot-stats-row label{white-space:nowrap}
            .bot-del{display:none !important}
          }
          .lt-show-m{display:none}
          .lt-sheet-handle{width:38px;height:4px;border-radius:2px;background:var(--border,#334155);margin:0 auto 10px}
          .lt-sheet{max-height:86dvh;overflow-y:auto;-webkit-overflow-scrolling:touch;animation:ltSheetUp .22s cubic-bezier(.2,.8,.2,1)}
          @keyframes ltSheetUp{from{transform:translateY(24px);opacity:.4}to{transform:none;opacity:1}}
        `}</style>
  </>
);
